import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateEntityVersionMutation,
  createEntityVersionDraftMutation,
  listEntityVersionsOptions,
  listEntityVersionsQueryKey,
} from '../generated/api/@tanstack/react-query.gen.js';
import { adaptMutation } from './adapt-mutation.js';

/**
 * Version history for any entity — the eleven types share one endpoint.
 *
 * The failure is surfaced here as well as rendered by the caller: a version list that comes back
 * empty because the request failed reads as "this entity has no versions", which is a different
 * and much more alarming statement than "we could not reach the server".
 */
export function useEntityVersions(entityId: number | undefined) {
  const query = useQuery({
    ...listEntityVersionsOptions({ path: { entityId: entityId ?? 0 } }),
    enabled: !!entityId,
  });
  if (query.isError) console.error('useEntityVersions: failed to load version history', query.error);
  return query;
}

export function useOpenEntityDraft() {
  const qc = useQueryClient();
  return useMutation({
    ...adaptMutation(
      createEntityVersionDraftMutation(),
      ({ entityId, changeSummary }: { entityId: number; changeSummary?: string }) => ({
        path: { entityId },
        body: { changeSummary },
      }),
    ),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: listEntityVersionsQueryKey({ path: { entityId: vars.entityId } }) }),
  });
}

/** Promote a draft or roll back to an earlier locked version — the same pointer move. */
export function useActivateEntityVersion() {
  const qc = useQueryClient();
  return useMutation({
    ...adaptMutation(
      activateEntityVersionMutation(),
      ({ entityId, versionId }: { entityId: number; versionId: number }) => ({ path: { entityId, versionId } }),
    ),
    onSuccess: (_, vars) =>
      qc.invalidateQueries({ queryKey: listEntityVersionsQueryKey({ path: { entityId: vars.entityId } }) }),
  });
}
